import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BookOpen, Calendar, Loader2, LogIn, Mail, MessageCircle, Send, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

import chatbotAvatar from '@/assets/chatbot-avatar.png';

const CodeoIcon = ({ size = 40 }: { size?: number }) => (
  <img 
    src={chatbotAvatar} 
    alt="Codeo" 
    className="shrink-0"
    style={{ width: size, height: size }}
  />
);
import { blogPosts } from '@/data/blogPosts';
import ChatMessageContent from './ChatMessageContent';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: Array<{
    label: string;
    icon: React.ComponentType<any>;
    action: () => void;
  }>;
}

const GlobalChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWidget, setShowWidget] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        role: 'assistant',
        content: `👋 Salut ! Moi c'est Codeo, votre assistant IA personnel de NoCodeo !\n\nJe suis là pour vous accompagner et vous aider à :\n• Découvrir nos formations no-code\n• Comprendre le fonctionnement de la plateforme\n• Vous assister durant vos formations\n• Vous orienter vers les bonnes ressources\n• Répondre à toutes vos questions\n\nAlors, comment puis-je vous aider aujourd'hui ? 😊`,
        timestamp: new Date(),
        actions: [
          {
            label: 'Voir formations',
            icon: BookOpen,
            action: () => navigate('/formations')
          },
          {
            label: 'Prendre RDV',
            icon: Calendar,
            action: () => navigate('/conseil')
          },
          {
            label: 'Se connecter',
            icon: LogIn,
            action: () => navigate('/auth')
          }
        ]
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, navigate]);

  const sendMessage = async (content?: string) => {
    const messageText = content || inputValue.trim();
    if (!messageText || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Pour le chatbot global, on peut fonctionner sans authentification
      const { data, error } = await supabase.functions.invoke('chat-assistant', {
        body: {
          messages: [...messages, userMessage].map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          chatType: 'global',
          userContext: {
            blogPosts: blogPosts
              .filter(p => p.status === 'published')
              .map(p => ({ title: p.title, slug: p.slug, category: p.category }))
          }
        }
      });

      if (error) throw error;

      // Détecter les actions suggérées dans la réponse
      let actions: Message['actions'] = [];
      const responseText = data.message;
      
      if (responseText.includes('catalogue') || responseText.includes('formations')) {
        actions.push({
          label: 'Voir le catalogue',
          icon: BookOpen,
          action: () => navigate('/formations')
        });
      }
      
      if (responseText.includes('RDV') || responseText.includes('consulting')) {
        actions.push({
          label: '📆 Prendre RDV',
          icon: Calendar,
          action: () => navigate('/conseil')
        });
      }
      
      if (responseText.includes('connecter') || responseText.includes('connexion')) {
        actions.push({
          label: '🔐 Se connecter',
          icon: LogIn,
          action: () => navigate('/auth')
        });
      }
      
      if (responseText.includes('contact') || responseText.includes('support')) {
        actions.push({
          label: 'Nous contacter',
          icon: Mail,
          action: () => navigate('/contact')
        });
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: responseText,
        timestamp: new Date(),
        actions: actions.length > 0 ? actions : undefined
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Erreur chat global:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de contacter l\'assistant. Réessayez.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "Quelles formations proposez-vous ?",
    "Comment s'inscrire ?",
    "Quels sont les prix ?",
    "Formations à distance ?"
  ];

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Widget réduit ou masqué
  if (!showWidget) {
    return (
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          onClick={() => setShowWidget(true)}
          size="sm"
          variant="outline"
          className="rounded-full h-8 w-8 p-0 shadow-md bg-white/90 backdrop-blur-sm"
          title="Afficher le chatbot"
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-50 group">
        {/* Bouton pour masquer temporairement */}
        <Button
          onClick={() => setShowWidget(false)}
          size="sm"
          variant="ghost"
          className="absolute -top-2 -left-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 bg-gray-500 hover:bg-gray-600 text-white rounded-full text-xs"
          title="Masquer temporairement"
        >
          ×
        </Button>
        
        {/* Label removed - avatar image is self-explanatory */}
        
        <button
          onClick={() => setIsOpen(true)}
          className="h-16 w-16 z-50 transition-all duration-300 hover:scale-110 cursor-pointer p-0 bg-transparent border-none"
          title="Discuter avec Codeo, notre chatbot IA"
        >
          <CodeoIcon size={64} />
        </button>
      </div>
    );
  }

  // Chat minimisé
  if (isMinimized) {
    return (
      <Card className="fixed bottom-4 left-4 w-80 shadow-lg z-50 border border-border/50">
        <CardHeader className="pb-2 bg-secondary text-secondary-foreground">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <CodeoIcon size={24} />
              Codeo
            </CardTitle>
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsMinimized(false)}
                className="text-secondary-foreground/70 hover:bg-secondary-foreground/10 h-6 w-6 p-0"
                title="Agrandir"
              >
                ↗
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsOpen(false)}
                className="text-secondary-foreground/70 hover:bg-secondary-foreground/10 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question..."
              disabled={isLoading}
              className="flex-1 h-8 text-sm"
            />
            <Button 
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              size="sm"
              className="h-8 w-8 p-0"
            >
              <Send className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fixed bottom-4 left-4 w-80 h-[500px] shadow-lg z-[60] flex flex-col border border-border/50">
      <CardHeader className="pb-3 flex-shrink-0 bg-secondary text-secondary-foreground">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CodeoIcon size={32} />
            Codeo
          </CardTitle>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsMinimized(true)}
              className="text-secondary-foreground/70 hover:bg-secondary-foreground/10 h-8 w-8 p-0"
              title="Réduire"
            >
              −
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsOpen(false)}
              className="text-secondary-foreground/70 hover:bg-secondary-foreground/10 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-4 pt-0 min-h-0">
        {/* Zone de messages avec scroll */}
        <ScrollArea className="flex-1 pr-3 mb-3">
          <div className="space-y-3">
            {messages.map((message, index) => (
              <div key={index}>
                <div
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    <div className="text-sm prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline"><ChatMessageContent content={message.content} /></div>
                    <div className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Actions suggérées */}
                {message.actions && message.actions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2 justify-start">
                    {message.actions.map((action, actionIndex) => (
                      <Button
                        key={actionIndex}
                        variant="outline"
                        size="sm"
                        onClick={action.action}
                        className="h-auto py-1 px-2 text-xs gap-1"
                      >
                        <action.icon className="h-3 w-3" />
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Assistant en train d'écrire...
                </div>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </ScrollArea>

        {/* Questions rapides - seulement au début */}
        {messages.length === 1 && (
          <div className="space-y-2 mb-3 flex-shrink-0">
            <div className="text-xs text-muted-foreground text-center">Questions fréquentes :</div>
            <div className="grid grid-cols-2 gap-1">
              {quickQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage(question)}
                  disabled={isLoading}
                  className="h-auto p-2 text-xs leading-tight whitespace-normal break-words min-h-[2.5rem]"
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Zone de saisie - TOUJOURS VISIBLE EN BAS */}
        <div className="border-t pt-3 flex-shrink-0">
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button 
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              size="sm"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GlobalChatbot;